import { PermissionService } from '../services/permissions.service';

export interface CampaignLockTarget {
  cncManager?: string[] | null;
  projectLeaders?: string[] | null;
}

/**
 * Campaign "locked" rule: CORE_CAMPAIGN update-capability holders are always unlocked; manager-listed
 * campaigns additionally unlock for admins/AMs and listed users; otherwise admins/AMs only.
 */
export function isCampaignLocked(
  permissionService: PermissionService,
  campaign: CampaignLockTarget | null | undefined,
  username: string | undefined
): boolean {
  if (permissionService.canUpdateCampaign()) {
    return false;
  }
  if (campaign?.cncManager?.length || campaign?.projectLeaders?.length) {
    return !(
      permissionService.isPlatformAdminOrAccountManager() ||
      campaign.cncManager?.some((c: string) => c === username) ||
      campaign.projectLeaders?.some((l: string) => l === username)
    );
  }
  return !permissionService.isPlatformAdminOrAccountManager();
}

/**
 * Participant roles and workspace membership are no longer serialized, so only the project
 * manager row is transferable (by admins/account managers or the manager themselves).
 */
export function canTransferProjectManager(
  permissionService: PermissionService,
  project: { manager?: string } | null | undefined,
  rowUsername: string | undefined,
  authUserName: string | null
): boolean {
  // `==` intentional: preserves the null == undefined edge the original cells relied on
  return project?.manager === rowUsername && (permissionService.isAccountManager() || authUserName == rowUsername);
}

/**
 * "Nobody loses access" combinator for a project-row action gate (matches isCampaignLocked /
 * canTransferProjectManager's fallback shape): the existing global capability OR the client-side
 * ownership guess OR the new per-item batch-resolved grant. Additive only.
 */
export function canManageProjectItem(
  isAdminUser: boolean,
  isAccountOwner: boolean,
  project: { manager?: string } | null | undefined,
  authUserName: string | null | undefined,
  hasGlobalCapability: boolean,
  hasItemCapability: boolean
): boolean {
  return isAdminUser || isAccountOwner || project?.manager == authUserName || hasGlobalCapability || hasItemCapability;
}

/** True when the viewer may not annotate: not a review-link session and lacking admin/account-manager rights. */
export function checkInvalidUser(permissionService: PermissionService, reviewLink: unknown): boolean {
  return !reviewLink && !permissionService.isPlatformAdminOrAccountManager();
}
